import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Col, Flex, Pagination, PaginationProps, Row, Select, Spin,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ProjectCardItem from '../card/ProjectCardItem';
import { useResizeWidthWindow } from '../hook/useResizeWidthWindow';
import data from '../../awards_data.json';
import { SponsorsBlock } from '../SponsorsBlock';

interface TabProjectAwardsProps {
}

const FlexStyled = styled(Flex)`
  align-items: stretch;
`;

const DivFilterStyled = styled.div`
  margin: 20px 0px;
  align-items: stretch;

  @media (max-width: 768px) {
    margin: 10px 0px;
  }
`;

const FlexFilterStyled = styled(Flex)`
  padding: 20px;
  border: 1px solid rgba(115, 115, 115, 0.2);
  background-color: #FAFAFA;
  border-radius: 4px;
`;

const FlexPaginatorStyled = styled(Flex)`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 10px;
  }
`;

export const TabProjectAwards: React.FC<TabProjectAwardsProps> = () => {
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string[]>([]);
  const [spin, setSpin] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [newPageSize, setNewPageSize] = useState(9);
  const startIndex = (currentPage - 1) * newPageSize;
  const endIndex = startIndex + newPageSize;

  const filteredAwards = useMemo(() => Object.values(data).filter((award) => {
    const matchYear = selectedYears.length === 0 || selectedYears.includes(award.year);
    const matchCategory = selectedCategory.length === 0 || selectedCategory.includes(award.category);
    const matchCompany = selectedCompany.length === 0 || selectedCompany.includes(award.woodworker);

    return matchYear && matchCategory && matchCompany;
  }), [selectedYears, selectedCategory, selectedCompany]);

  const paginatedData = filteredAwards.slice(startIndex, endIndex);

  const selectYears = Array.from(new Set(Object.values(data).map((item) => item.year))).sort((a, b) => b - a);

  const optionsYears = selectYears.map((category) => ({
    label: category,
    value: category,
  }));

  const selectCategory = Array.from(
    new Set(Object.values(data).map((item) => item.category)),
  ).filter(Boolean);

  const optionsCategory = selectCategory.map((category) => ({
    label: category,
    value: category,
  }));

  const selectCompany = Array.from(
    new Set(Object.values(data).map((item) => item.woodworker)),
  )
    .filter((name): name is string => !!name && name.trim() !== '')
    .sort((a, b) => a.localeCompare(b));

  const optionsCompany = selectCompany.map((company) => ({
    label: company,
    value: company,
  }));

  const handleYearChange = (value: number[]) => {
    setSpin(true);
    setTimeout(() => setSpin(false), 500);
    setSelectedYears(value);
  };

  const handleCategoryChange = (value: string[]) => {
    setSpin(true);
    setTimeout(() => setSpin(false), 500);
    setSelectedCategory(value);
  };

  const handleCompanyChange = (value: string[]) => {
    setSpin(true);
    setTimeout(() => setSpin(false), 500);
    setSelectedCompany(value);
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (current, pageSize) => {
    setCurrentPage(current);
    setNewPageSize(pageSize);
  };

  const showTotal: PaginationProps['showTotal'] = (total) => `Total ${total} projects`;

  return (
      <Row gutter={[24, 24]} align="stretch">
          <Col className="gutter-row" xs={20} sm={20} md={20} lg={20} xl={20}>
              <DivFilterStyled>
                  <FlexFilterStyled vertical gap={10}>
                      <Select
                          mode="tags"
                          size={'middle'}
                          placeholder="Select a year"
                          defaultValue={[]}
                          onChange={handleYearChange}
                          style={{ width: '100%' }}
                          options={optionsYears}
                      />
                      <Select<string[]>
                          mode="tags"
                          size={'middle'}
                          placeholder="Select a category"
                          defaultValue={[]}
                          onChange={handleCategoryChange}
                          style={{ width: '100%' }}
                          options={optionsCategory}
                      />
                      <Select
                          showSearch
                          mode="multiple"
                          size={'middle'}
                          placeholder="Select a company"
                          optionFilterProp="label"
                          onChange={handleCompanyChange}
                          options={optionsCompany}
                          style={{ width: '100%' }}
                      />
                  </FlexFilterStyled>
              </DivFilterStyled>
              <Spin spinning={spin} style={{ minHeight: 400 }} indicator={<LoadingOutlined style={{ fontSize: 100 }}/>}>
                          <FlexStyled justify={'space-between'} align={'center'} wrap={'wrap'} gap={20}>
                              {paginatedData.map((project) => <ProjectCardItem key={project.id} dataCard={project}/>)}
                          </FlexStyled>
                          <FlexPaginatorStyled justify={'center'}>
                              <Pagination
                                  showSizeChanger
                                  onChange={onShowSizeChange}
                                  onShowSizeChange={onShowSizeChange}
                                  defaultCurrent={1}
                                  total={filteredAwards.length}
                                  showTotal={showTotal}
                                  pageSizeOptions={[10, 20, 30]}
                                  defaultPageSize={10}
                                  size={useResizeWidthWindow() < 700 ? 'small' : 'default'}
                              />
                          </FlexPaginatorStyled>

              </Spin>
          </Col>
          <Col className="gutter-row" xs={4} sm={4} md={4} lg={4} xl={4}>
              <SponsorsBlock />
          </Col>
      </Row>
  );
};
